/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMachine } from '@xstate/react';
import { useCurrentStateAndParams, useRouter } from '@uirouter/react';
import { ExtJS } from '../../../../../../interface/ExtJS';
import { FIREWALL_EVALUATIONS_ACTION_PERMISSIONS } from '../../../../shared/firewallSecurityPermissions';
import { evaluationsListMachine } from './machines/evaluationsListMachine';
import {
  FILTER_IDS,
  THREAT_LEVELS,
  type EvaluationSortField,
  type EvaluationsQuery,
  type ThreatLevel,
  type ViolationsFilter,
} from './evaluations.types';
import { parseEvaluationsUrl, serializeEvaluationsUrl } from './evaluationsUrl';

const isThreatLevel = (value: string): value is ThreatLevel => (THREAT_LEVELS as readonly string[]).includes(value);

/** Only one of "with" and "without" can apply, so the option just ticked replaces the other one. */
function violationsFromSelection(values: string[], current: ViolationsFilter): ViolationsFilter {
  const added = values.filter((value) => value !== current);
  const last = added[added.length - 1];
  return last === 'with' || last === 'without' ? last : null;
}

/** The URL is the source of truth for the query: every change goes to the URL and the machine follows it. */
export function useEvaluationsList() {
  const router = useRouter();
  const { params } = useCurrentStateAndParams();
  const query = useMemo(() => parseEvaluationsUrl(params), [params]);

  // Mutators read the latest query from here, so their identity stays stable.
  const latest = useRef(query);
  latest.current = query;

  const update = useCallback(
    (patch: Partial<EvaluationsQuery>, resetPage = true) => {
      const next = { ...latest.current, ...patch, page: resetPage ? 1 : (patch.page ?? latest.current.page) };
      // The URL lags the navigation, so a second change in the same tick must build on this one.
      latest.current = next;
      router.stateService.go('.', serializeEvaluationsUrl(next), { location: 'replace' });
    },
    [router]
  );

  // Hides the re-evaluate action only; the backend enforces the same permission.
  const canEvaluate = ExtJS.usePermission(
    () => ExtJS.checkPermission(FIREWALL_EVALUATIONS_ACTION_PERMISSIONS.evaluate),
    []
  );

  const [state, send] = useMachine(evaluationsListMachine, {
    context: { query, searchInput: query.search, committedSearch: query.search },
    actions: { commitSearch: (context) => update({ search: context.committedSearch }) },
  });
  const { data, error, searchInput } = state.context;

  useEffect(() => {
    send({ type: 'SET_QUERY', query });
  }, [query, send]);

  const loading = state.matches('loading');
  const hasData = data !== null;
  const total = data?.total ?? 0;
  const filtered = data?.filtered ?? 0;
  const failed = state.matches('failed');

  return {
    query,
    canEvaluate,
    hasData,
    rows: data?.results ?? [],
    total,
    filtered,
    formats: data?.formats ?? [],
    loading,
    failed,
    error,
    firstLoad: loading && !hasData,
    reloading: loading && hasData,
    showHeader: hasData && !failed && total > 0,
    totalPages: Math.max(1, Math.ceil(filtered / query.pageSize)),
    hasActiveFilters:
      query.formats.length > 0 || query.threatLevels.length > 0 || query.violations !== null || query.search !== '',
    searchInput,
    setSearchInput: useCallback((value: string) => send({ type: 'TYPE_SEARCH', value }), [send]),
    /** One command for every filter section, named by FILTER_IDS. An unknown section changes nothing. */
    changeFilter: useCallback(
      (sectionId: string, value: string | string[]) => {
        const values = Array.isArray(value) ? value : [value];
        if (sectionId === FILTER_IDS.format) {
          update({ formats: values });
        } else if (sectionId === FILTER_IDS.threatLevel) {
          update({ threatLevels: values.filter(isThreatLevel) });
        } else if (sectionId === FILTER_IDS.violations) {
          update({ violations: violationsFromSelection(values, latest.current.violations) });
        }
      },
      [update]
    ),
    setSort: useCallback((sortBy: EvaluationSortField, asc: boolean) => update({ sortBy, asc }), [update]),
    setPage: useCallback((page: number) => update({ page }, false), [update]),
    setPageSize: useCallback((pageSize: number) => update({ pageSize }), [update]),
    clearFilters: useCallback(() => {
      send({ type: 'RESET_SEARCH' });
      update({ formats: [], threatLevels: [], violations: null, search: '' });
    }, [send, update]),
    retry: useCallback(() => send({ type: 'RETRY' }), [send]),
    /** Loads the current query again, for example after a re-evaluation ended. */
    refresh: useCallback(() => send({ type: 'REFRESH' }), [send]),
  };
}
